import { List } from "immutable";
import * as React from "react";
import ReactResizeDetector from "react-resize-detector";

import { ElementHtmlAttributes, getAttributes, isKeyOrDigit } from "../common/common";
import {
  ColumnProportionEm,
  GridOperations,
  OrderByItem,
  TAlignment,
  UpdateControl,
  UpdateInnerDataGrid,
  UpdateQuickFilter,
  UpdateAggregationPanel,
  CSAggrData,
  CSColumn,
  UpdateDataGrid,
  TTreeDataHasChildNodes,
  TUFContextDisplayStyle,
  ColumnsProportion,
  CondFormattingStyle,
  cJSonCommandExecuteLookup,
  CSDataItem,
  MFMeasure,
  TUpdateValueState,
  MFValue,
} from "../common/communication.base";
import {
  NclDataGrid,
  NclDataGridContent,
  NclDataGridFooter,
  NclImage,
  NclInnerDataGrid,
  NclInput,
  NclQuickFilter,
  UFNclControlBase,
  NclAggregationPanel,
  DataGridListener,
  UFOverAlign,
  UFOverAlignListener,
} from "../common/components.ncl";
import { K2Header } from "./K2Expander";
import { AcquireControl, K2ComponentState, StyleHelper, withContext, WithContextPlacementProps, WithVCXProps } from "./k2hoc";
import { K2Img } from "./K2Image";
import { K2RuleWithVCXProps, getTextDecoration } from "./k2StyleRenderer";
import { K2ToolBar } from "./K2ToolBar";
import { K2TruncateText } from "./K2TruncateText";
import { ColumnBaseHelper, _Input } from "./K2Input";
import { GenerateControl } from "./K2GenerateControl";
import { KeyboardEventHandler } from "react";
import { K2MFText } from "./K2MultiFormatText";
import { RealizerOperations, ViewRealizerManager } from "../viewrealizer";
import { Context } from "../appcontext";
import { UFInteriorMargin } from "../common/visualContext";

type K2ColumnRuleProps = K2RuleWithVCXProps & {
  proportion: ColumnProportionEm;
  fixed: boolean;
  borderLeft?: boolean;
  borderRight?: boolean;
  borderTop?: boolean;
  borderBottom?: boolean;
  offset: number;
};

const dgColumnBaseRule = (props: K2ColumnRuleProps) => ({
  borderColor: props.vcx.getColor(props.vcx.Data.ColorMap.ContentFrame1),
  borderStyle: "solid",
  borderLeftWidth: props.borderLeft !== false ? "1px" : 0,
  borderRightWidth: props.borderRight !== false ? "1px" : 0,
  borderTopWidth: props.borderTop !== false ? "1px" : 0,
  borderBottomWidth: props.borderBottom !== false ? "1px" : 0,
  backgroundColor: props.fixed ? props.vcx.getColor(props.vcx.Data.ColorMap.ContentColorBck1) : "unset",
  position: props.fixed ? ("absolute" as "absolute") : null,
  width: props.fixed ? props.proportion.Width : null,
  height: props.fixed ? props.vcx.GridControl.GetRowHeight(1) + "px !important" : null,
  zIndex: props.fixed ? 1 : null,
  left: props.fixed ? props.offset + "px" : null,
});
const column_padding_x = 6;
const dgHeaderColumnRule = (props: K2ColumnRuleProps) => ({
  padding: `0px ${column_padding_x}px`,
  ">div": {
    justifyContent:
      props.proportion && props.proportion.DisplayStyleRT !== TUFContextDisplayStyle.cdsImage ? getContentAlign(props.proportion.Alignment) : "center",
  },
});

const handle = (props: K2RuleWithVCXProps) => ({
  position: "absolute" as const,
  width: "10px",
  height: "100%",
  bottom: 0,
  right: "-5px",
  cursor: "col-resize",
  zIndex: 1,
  ">div": {
    height: "100%",
    width: "1px",
    margin: "auto",
  },
  small: {
    width: "25px",
    right: "-13px",
  },
});

const lastColumnHandle = (props: K2RuleWithVCXProps) => ({
  right: "0 !important",
  ">div": {
    marginLeft: "10px",
  },
});

export interface BaseColumnProps extends WithVCXProps {
  columnProportion: ColumnProportionEm;
  onColumnContextMenu: ColumnContextMenu;
  onColumnClick: ColumnClick;
  fixed: boolean;
  lastColumn?: boolean;
  isTreeCell?: boolean;
  columnOffset?: number;
  columnNdx: number;
  rowHeightMultiplier?: number;
}

export interface HeaderColumnProps extends BaseColumnProps {
  orderBy?: number;
  orderByCount?: number;
  onColumnResize?: ColumnResize;
  setColumns?: (options: { sizes: DOMRect[]; draggedColumnIndex: number }) => void;
  deleteColumn?: () => void;
  editingColumns?: (editing: boolean) => boolean;
}

interface HeaderColumnState {
  isResizing?: boolean;
  translateX?: number;
}

export class HeaderColumn extends React.PureComponent<HeaderColumnProps, HeaderColumnState> {
  static displayName = `K2HeaderColumn`;
  prevTouch: TouchList;

  constructor(props: HeaderColumnProps) {
    super(props);
    this.state = { translateX: 0 };
  }
  private getRule(): string {
    let border = {};
    if (this.props.isTreeCell) {
      border = {
        borderLeft: false,
        borderTop: false,
      };
    }
    let rule: string = this.props.renderer.renderRule(
      this.props.renderer.combineRule(dgColumnBaseRule, dgHeaderColumnRule),
      Object.assign({}, { fixed: this.props.fixed, proportion: this.props.columnProportion, vcx: this.props.vcx, offset: this.props.columnOffset }, border)
    );
    return rule;
  }

  private getOrderInfo = (): JSX.Element => {
    let icon: string = "wui*arrow.down";

    if (this.props.orderBy > 0) {
      icon = "wui*arrow.up";
    }

    if (this.props.orderByCount > 1 && Math.abs(this.props.orderBy) > 0) {
      return (
        <div>
          <K2Img glyphId={icon} vcx={this.props.vcx} height={19} width={19} strokeColor={this.props.vcx.getColor(this.props.vcx.Data.ColorMap.ContentFrame3)} />
          <span style={{ fontSize: "70%", marginTop: "20%" }}>{Math.abs(this.props.orderBy)}</span>
        </div>
      );
    } else if (Math.abs(this.props.orderBy) > 0) {
      return (
        <div>
          <K2Img glyphId={icon} vcx={this.props.vcx} height={19} width={19} strokeColor={this.props.vcx.getColor(this.props.vcx.Data.ColorMap.ContentFrame3)} />
        </div>
      );
    }
    return null;
  };

  render() {
    if (!this.props.vcx || !this.props.renderer) return <th style={{ background: "inherit" }}></th>;

    return (
      <th className={this.getRule()} style={{ position: this.props.fixed ? null : "relative" }}>
        <div
          draggable={!this.props.fixed && !(this.props.isTreeCell && this.props.columnNdx === 0)}
          onContextMenu={this.handleContextMenu}
          onClick={this.handleClick}
          onDragStart={this.handleDragStart}
          onDragEnd={this.handleDragEnd}
          onTouchStart={this.handleDragStart}
          onTouchEnd={this.handleDragEnd}
        >
          <K2TruncateText
            className={this.props.renderer.renderFontRule(this.props.vcx.GridControl.HeaderFont, this.props.vcx)}
            style={{ whiteSpace: "nowrap" }}
          >
            {this.props.columnProportion.Caption}
          </K2TruncateText>
          {this.getOrderInfo()}
        </div>
        {!this.props.fixed && (
          <span
            className={
              this.props.lastColumn
                ? this.props.renderer.renderRule(handle, { vcx: this.props.vcx }) +
                  " " +
                  this.props.renderer.renderRule(lastColumnHandle, { vcx: this.props.vcx })
                : this.props.renderer.renderRule(handle, { vcx: this.props.vcx })
            }
            style={{ transform: `translateX(${this.state.translateX}px)` }}
            onMouseDown={this.handleMouseDown}
            onTouchStart={this.handleMouseDown}
          >
            {(this.state.isResizing || this.state.translateX !== 0) && (
              <div style={{ borderRight: `1px solid ${this.props.vcx.getColor(this.props.vcx.Data.ColorMap.ContentNormalColorFrg)}` }} />
            )}
          </span>
        )}
      </th>
    );
  }

  private handleDragStart = (e: React.DragEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (!this.canEditColumns(e)) return;

    let nodes = Array.from(e.currentTarget.parentElement.parentElement.children).map((node) => {
      return node.getBoundingClientRect();
    });

    let draggedColumnIndex = (e.currentTarget.parentElement as HTMLTableHeaderCellElement).cellIndex;
    this.props.setColumns({ sizes: nodes, draggedColumnIndex: draggedColumnIndex });
  };

  private handleDragEnd = (e: React.DragEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    let yPos = 0;

    if (e.nativeEvent instanceof DragEvent) {
      yPos = (e as React.DragEvent).clientY;
    } else {
      yPos = (e as React.TouchEvent).changedTouches[0].clientY;
    }

    if (yPos < e.currentTarget.getBoundingClientRect().top) {
      this.props.deleteColumn();
    }
  };

  private canEditColumns(e: React.MouseEvent | React.TouchEvent): boolean {
    if (this.props.editingColumns) {
      if (this.props.editingColumns(true)) {
        return true;
      }
    }
    e.preventDefault();
    return false;
  }

  private handleMouseDown = (event: React.MouseEvent<HTMLSpanElement> | React.TouchEvent) => {
    if (!this.canEditColumns(event)) return;

    this.setState({ isResizing: true });
    document.body.style.cursor = "col-resize";
  };

  componentDidUpdate(prevProps: HeaderColumnProps, prevState: HeaderColumnState) {
    if (prevState.isResizing !== this.state.isResizing) {
      if (this.state.isResizing == true) {
        document.addEventListener("mousemove", this.handleMouseMove);
        document.addEventListener("mouseup", this.handleMouseUp);
        document.addEventListener("touchmove", this.handleMouseMove);
        document.addEventListener("touchend", this.handleMouseUp);
      } else {
        document.removeEventListener("mousemove", this.handleMouseMove);
        document.removeEventListener("mouseup", this.handleMouseUp);
        document.removeEventListener("touchmove", this.handleMouseMove);
        document.removeEventListener("touchend", this.handleMouseUp);
      }
    }
  }

  private handleMouseMove = (e: MouseEvent | TouchEvent) => {
    if (e instanceof MouseEvent) {
      this.setState({ translateX: this.state.translateX + e.movementX / window.devicePixelRatio });
      if (this.props.onColumnResize && e.movementX !== 0) {
        this.props.onColumnResize.call(this, this.props.columnNdx, e.movementX, false);
      }
    } else {
      let movementX: number;

      if (this.prevTouch) {
        movementX = e.touches[0].pageX - this.prevTouch[0].pageX;
      } else {
        movementX = 0;
      }

      this.setState({ translateX: this.state.translateX + movementX });
      this.prevTouch = e.touches;

      if (this.props.onColumnResize && movementX !== 0) {
        this.props.onColumnResize.call(this, this.props.columnNdx, movementX, false);
      }
    }
  };

  private handleMouseUp = (e: MouseEvent | TouchEvent) => {
    let diff = this.state.translateX;
    this.setState({ isResizing: false, translateX: 0 });
    document.body.style.cursor = "";
    if (this.props.onColumnResize && diff !== 0) {
      this.props.onColumnResize.call(this, this.props.columnNdx, diff, true);
    }
    e.preventDefault();
    this.prevTouch = null;
  };

  private timeout: any;
  private clickCount: number = 0;

  componentWillUnmount() {
    if (this.timeout) clearTimeout(this.timeout);
  }

  private handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (this.props.onColumnClick) {
      if (this.timeout) clearTimeout(this.timeout);

      this.clickCount++;
      let shiftKey = e.shiftKey;

      this.timeout = setTimeout(() => {
        if (this.clickCount >= 2) {
          this.props.onColumnClick.call(this, -1, this.props.columnNdx, 2, shiftKey);
        } else {
          this.props.onColumnClick.call(this, -1, this.props.columnNdx, 1, shiftKey);
        }
        this.clickCount = 0;
      }, 250);
      e.preventDefault();
    }
  };

  private handleContextMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    if (this.props.onColumnContextMenu) {
      this.props.onColumnContextMenu.call(this, -1, this.props.columnNdx, null);
    }
    e.preventDefault();
  };
}

export const K2HeaderColumn = withContext(HeaderColumn);

const dgHeaderBaseRule = (props: K2RuleWithVCXProps) => ({
  height: props.vcx.GridControl.GetRowHeight(1) + "px",
});

const dgHeaderRule = (props: K2RuleWithVCXProps) => ({
  background: props.vcx.getColor(props.vcx.Data.ColorMap.ContentColorBck1),
  color: props.vcx.getColor(props.vcx.Data.ColorMap.ContentDecorateColorFrg),
});

type FixedColumnOffset = (columnsWidths: number[], index: number, fixed: boolean) => number;

interface ColumnsProps {
  columnsProportion: Array<ColumnProportionEm>;
  columnsWidths: number[];
  columnsVersion: number;
  fixedColumnsCount: number;
}

export type ColumnClick = (rowIndex: number, colIndex: number, clickCount: number, shiftKey: boolean) => void;
export type ColumnContextMenu = (rowIndex: number, colIndex: number) => void;
export type ColumnResize = (colIndex: number, diffX: number, isCompleted: boolean) => void;

interface DGHeaderProps extends WithVCXProps, ColumnsProps {
  onColumnContextMenu: ColumnContextMenu;
  onColumnClick: ColumnClick;
  onColumnResize?: ColumnResize;
  setColumns?: (options: { sizes: DOMRect[]; draggedColumnIndex: number }) => void;
  deleteColumn?: () => void;
  getFixedColumnOffset: FixedColumnOffset;
  editingColumns?: (editing: boolean) => boolean;
}

interface HeaderProps extends DGHeaderProps {
  orderBy: Array<OrderByItem>;
}

function compareArray(array: Array<any>, newArray: Array<any>) {
  if (array) {
    if (newArray) {
      if (array.length === newArray.length) {
        return true;
      }
    }
  }

  return false;
}

function compareNumberArray(array: Array<number>, newArray: Array<number>) {
  if (compareArray(array, newArray)) {
    let result = true;
    array.forEach((value, index) => {
      if (value !== newArray[index]) {
        result = false;
        return;
      }
    });
    return result;
  }
  return false;
}

class DGHeader extends React.Component<HeaderProps> {
  static displayName = `K2DGHeader`;

  private compareOrderBy(value: Array<OrderByItem>, newValue: Array<OrderByItem>): boolean {
    let result = false;
    if (compareArray(value, newValue)) {
      result = true;
      value.map((item, index) => {
        if (item.ColNdx !== newValue[index].ColNdx || item.OrderByNdx !== newValue[index].OrderByNdx) {
          result = false;
          return true;
        }
      });
    }
    return result;
  }

  shouldComponentUpdate(nextProps: HeaderProps, nextState: any, nextContext: any): boolean {
    if (nextProps.columnsVersion !== this.props.columnsVersion) {
      return true;
    }

    if (!this.compareOrderBy(this.props.orderBy, nextProps.orderBy)) {
      return true;
    }

    if (!compareNumberArray(this.props.columnsWidths, nextProps.columnsWidths)) {
      return true;
    }

    return false;
  }

  render() {
    let isFixed: boolean;
    return (
      <tr
        className={
          this.props.renderer.renderFontRule(this.props.vcx.GridControl.HeaderFont, this.props.vcx) +
          " " +
          this.props.renderer.renderRule(dgHeaderBaseRule, { vcx: this.props.vcx }) +
          " " +
          this.props.renderer.renderRule(dgHeaderRule, { vcx: this.props.vcx })
        }
      >
        {this.props.columnsProportion.map((value, index, list) => {
          isFixed = index < this.props.fixedColumnsCount;
          let orderBy = this.props.orderBy.find((item) => {
            return item.ColNdx === index;
          });

          return (
            <K2HeaderColumn
              fixed={index < this.props.fixedColumnsCount}
              columnNdx={index}
              orderByCount={this.props.orderBy.length}
              orderBy={orderBy ? orderBy.OrderByNdx : undefined}
              onColumnClick={this.props.onColumnClick}
              onColumnContextMenu={this.props.onColumnContextMenu}
              onColumnResize={this.props.onColumnResize}
              key={value.Caption + index.toString() + "_hc"}
              columnProportion={value}
              vcx={this.props.vcx}
              lastColumn={index === list.length - 1}
              setColumns={this.props.setColumns}
              deleteColumn={this.props.deleteColumn}
              columnOffset={this.props.getFixedColumnOffset(this.props.columnsWidths, index, isFixed)}
              editingColumns={this.props.editingColumns}
            />
          );
        })}
      </tr>
    );
  }
}

const K2DGHeader = withContext(DGHeader);

interface QuickFilterProps extends WithContextPlacementProps, ColumnsProps {
  getFixedColumnOffset: FixedColumnOffset;
}

class _QuickFilter extends React.Component<QuickFilterProps, K2ComponentState<UpdateQuickFilter>> {
  static displayName = `K2QuickFilter`;
  private control: NclQuickFilter;

  constructor(props: QuickFilterProps) {
    super(props);
    this.control = AcquireControl(this.props.controlUID, this.props.vrUID, (ctrl) => {
      return ctrl instanceof NclQuickFilter;
    }) as NclQuickFilter;

    this.state = { data: this.control.init(this) as UpdateQuickFilter, vcxVersion: -1 };
  }

  updateState(state: UpdateControl) {
    this.setState((prevState: K2ComponentState<UpdateQuickFilter>) => {
      return { data: state as UpdateQuickFilter };
    });
  }

  updateVCX(vcxVersion: number) {
    this.setState({ vcxVersion: vcxVersion });
  }

  componentWillUnmount() {
    this.control.willUnMount(true);
    this.control = null;
  }

  shouldComponentUpdate(nextProps: QuickFilterProps, nextState: K2ComponentState<UpdateQuickFilter>, nextContext: any): boolean {
    if (nextProps.columnsVersion !== this.props.columnsVersion) {
      return true;
    }

    if (nextState !== this.state) {
      return true;
    }

    if (nextProps.fixedColumnsCount !== this.props.fixedColumnsCount) {
      return true;
    }

    if (!compareNumberArray(this.props.columnsWidths, nextProps.columnsWidths)) {
      return true;
    }

    return false;
  }

  render(): JSX.Element {
    if (!this.control.Filters || this.state.data.Visible !== true) return null;
    let filters = this.control.Filters;

    return (
      <tr tabIndex={1} className={this.props.renderer.renderRule(dgHeaderRule, { vcx: this.control.VCX })}>
        {filters.map((value: UFNclControlBase, index: number) => {
          let proportion: ColumnProportionEm = null;
          if (this.props.columnsProportion.length > index) {
            proportion = this.props.columnsProportion[index];

            return (
              <th
                key={"column_filter_" + index}
                className={this.props.renderer.renderRule(dgColumnBaseRule, {
                  fixed: index < this.props.fixedColumnsCount,
                  proportion: proportion,
                  vcx: this.control.VCX,
                  offset: this.props.getFixedColumnOffset(this.props.columnsWidths, index, index < this.props.fixedColumnsCount),
                })}
              >
                {GenerateControl(value, { minHeight: this.control.VCX.GridControl.GetRowHeight(1) + "px", padding: 0 })}
              </th>
            );
          } else {
            return null;
          }
        })}
      </tr>
    );
  }
}

const K2QuickFilter = withContext(_QuickFilter);

const aggrColumnRule = (props: K2RuleWithVCXProps) => ({
  padding: `0px ${column_padding_x}px`,
  border: "1px solid " + props.vcx.getColor(props.vcx.Data.ColorMap.BaseColorBck1),
  background: props.vcx.getColor(props.vcx.Data.ColorMap.DataBrowseColorBck),
  color: props.vcx.getColor(props.vcx.Data.ColorMap.DataBrowseColorFrg),
  height: "100%",
  width: "100%",
});

type AggrPanelProps = K2RuleWithVCXProps & {
  invalidData: Boolean;
};

const aggrPanelRule = (props: AggrPanelProps) => ({
  height: props.vcx.GridControl.GetRowHeight(1) + "px",
  width: "100%",
  opacity: props.invalidData ? 0.6 : 1,
  background: props.vcx.getColor(props.vcx.Data.ColorMap.ContentColorBck1),
});

interface AggregationPanelProps extends WithContextPlacementProps, ColumnsProps {
  width?: number;
  getFixedColumnOffset: FixedColumnOffset;
}

class _AggregationPanel extends React.Component<AggregationPanelProps, K2ComponentState<UpdateAggregationPanel>> {
  static displayName = `K2AggregationPanel`;
  private control: NclAggregationPanel;

  constructor(props: AggregationPanelProps) {
    super(props);
    this.control = AcquireControl(this.props.controlUID, this.props.vrUID, (ctrl) => {
      return ctrl instanceof NclAggregationPanel;
    }) as NclAggregationPanel;

    this.state = { data: this.control.init(this) as UpdateAggregationPanel, vcxVersion: -1 };
  }

  updateState(state: UpdateControl) {
    this.setState((prevState: K2ComponentState<UpdateAggregationPanel>) => {
      return { data: state as UpdateAggregationPanel };
    });
  }

  updateVCX(vcxVersion: number) {
    this.setState({ vcxVersion: vcxVersion });
  }

  componentWillUnmount() {
    this.control.willUnMount(true);
    this.control = null;
  }

  shouldComponentUpdate(nextProps: AggregationPanelProps, nextState: K2ComponentState<UpdateAggregationPanel>, nextContext: any): boolean {
    if (nextProps.columnsVersion !== this.props.columnsVersion) {
      return true;
    }

    if (nextState !== this.state) {
      return true;
    }

    if (!compareNumberArray(this.props.columnsWidths, nextProps.columnsWidths)) {
      return true;
    }

    if (nextProps.fixedColumnsCount !== this.props.fixedColumnsCount) {
      return true;
    }

    if (nextProps.width !== this.props.width) {
      return true;
    }

    return false;
  }

  render(): JSX.Element {
    if (!this.state.data.Data || this.state.data.Visible !== true) return null;
    let values: Array<CSAggrData> = this.state.data.Data.toJS();
    return (
      <div
        className={
          this.props.renderer.renderFontRule(this.control.VCX.GridControl.Font, this.control.VCX) +
          " " +
          this.props.renderer.renderRule(aggrPanelRule, { vcx: this.control.VCX, invalidData: this.state.data.InvalidData })
        }
      >
        <table tabIndex={1} className={this.props.renderer.renderRule(dgTableRule, { vcx: this.control.VCX, fillHeight: true, width: this.props.width })}>
          {this.props.columnsProportion && (
            <ColGroup
              columnsProportion={this.props.columnsProportion}
              fixedColumnsCount={this.props.fixedColumnsCount}
              columnsWidths={this.props.columnsWidths}
              columnsVersion={this.props.columnsVersion}
            />
          )}
          <tbody>
            <tr>
              {values.map((value: CSAggrData, index: number) => {
                let proportion: ColumnProportionEm = null;
                if (this.props.columnsProportion && this.props.columnsProportion.length > index) {
                  proportion = this.props.columnsProportion[index];

                  return (
                    <th
                      key={"column_aggr_" + index}
                      style={{ height: this.control.VCX.GridControl.GetRowHeight(1) + "px" }}
                      className={this.props.renderer.renderRule(dgColumnBaseRule, {
                        fixed: index < this.props.fixedColumnsCount,
                        proportion: proportion,
                        vcx: this.control.VCX,
                        offset: this.props.getFixedColumnOffset(this.props.columnsWidths, index, index < this.props.fixedColumnsCount),
                      })}
                      onContextMenu={(e) => this.handleContextMenu(e, index)}
                    >
                      <div className={this.props.renderer.renderRule(aggrColumnRule, { vcx: this.control.VCX })}>
                        {value.GlyphId && (
                          <K2Img
                            glyphId={value.GlyphId}
                            vcx={this.control.VCX}
                            height={this.control.VCX.sizeMap(16)}
                            width={this.control.VCX.sizeMap(16)}
                            strokeColor={this.control.VCX.getColor(this.control.VCX.Data.ColorMap.BaseColorBck1)}
                          />
                        )}
                        <K2TruncateText style={{ width: "100%", textAlign: getTextAlign(value.Align), alignSelf: "center", fontWeight: "normal" }}>
                          {value.Text.toString()}
                        </K2TruncateText>
                      </div>
                    </th>
                  );
                } else {
                  return null;
                }
              })}
            </tr>
          </tbody>
        </table>
      </div>
    );
  }
  private handleContextMenu = (e: React.MouseEvent<HTMLDivElement>, index: number) => {
    this.control.contextMenu(index);
    e.preventDefault();
  };
}

const K2AggregationPanel = withContext(_AggregationPanel);

const dgTBodyRule = (props: K2RuleWithVCXProps) => ({
  color: props.vcx.getColor(props.vcx.Data.ColorMap.DataBrowseColorFrg),
});

interface RowsProps extends DGHeaderProps {
  rows: Array<CSDataItem>;
  dataVersion: number;
  inEditMode: boolean;
  invokeAction: (op: GridOperations, diffPX: number) => void;
  onKeyDown: KeyboardEventHandler<HTMLTableSectionElement>;
  onKeyPress: KeyboardEventHandler<HTMLTableSectionElement>;
  rowHeightMultiplier: number;
  rowHeight: number;
  execute: (commandNumber: number) => void;
}

class Rows extends React.Component<RowsProps, {}> {
  private rows: Map<number, Row>;
  static displayName = `K2Rows`;
  private shiftFlag: boolean = false;

  constructor(props: RowsProps) {
    super(props);
    this.rows = new Map<number, Row>();
  }

  public getRow(index: number): Row {
    if (index >= 0 && this.rows && index < this.rows.size) {
      return this.rows.get(index);
    }
    return null;
  }

  shouldComponentUpdate(nextProps: RowsProps, nextState: {}, nextContext: any): boolean {
    if (nextProps.columnsVersion !== this.props.columnsVersion) {
      return true;
    }

    if (nextProps.rowHeightMultiplier !== this.props.rowHeightMultiplier) {
      return true;
    }

    if (nextProps.rowHeight !== this.props.rowHeight) {
      return true;
    }

    if (nextProps.dataVersion !== this.props.dataVersion) {
      return true;
    }

    if (nextProps.inEditMode !== this.props.inEditMode) {
      return true;
    }

    if (nextProps.fixedColumnsCount !== this.props.fixedColumnsCount) {
      return true;
    }

    if (!compareNumberArray(this.props.columnsWidths, nextProps.columnsWidths)) {
      return true;
    }

    return false;
  }

  render() {
    this.rows.clear();
    return (
      <tbody
        onTouchStart={this.handleTouchStart}
        onTouchMove={this.handleTouchMove}
        onKeyDown={this.handleKeyDown}
        onKeyPress={this.props.onKeyPress}
        onKeyUp={this.handleKeyUp}
        onWheel={this.handleWhell}
        className={
          this.props.renderer.renderRule(dgTBodyRule, { vcx: this.props.vcx }) +
          " " +
          this.props.renderer.renderFontRule(this.props.vcx.GridControl.Font, this.props.vcx)
        }
      >
        {this.props.rows?.map((value: CSDataItem, index: number) => {
          if (value) {
            return (
              <K2Row
                {...this.props}
                rowNdx={value.Row}
                rowHeightMultiplier={this.props.rowHeightMultiplier}
                rowHeight={this.props.rowHeight}
                key={`Row_${value.Row}`} //_${value.Key}
                data={value}
                vcx={this.props.vcx}
                ref={(ref) => {
                  if (ref && ref instanceof Row) this.rows.set(index, ref);
                }}
                execute={this.props.execute}
              />
            );
          } else {
            return null;
          }
        })}
      </tbody>
    );
  }

  //Posouvání kurzoru v DataGridu pomocí gesta(myš/dotyk)
  private lastYPosition: number = -100;

  private touchHandler(value: number): boolean {
    if (this.lastYPosition > -100 && value >= 0) {
      let touchDistance = this.lastYPosition - value;
      touchDistance < 0
        ? this.props.invokeAction(GridOperations.prior, Math.abs(touchDistance))
        : this.props.invokeAction(GridOperations.next, Math.abs(touchDistance));
      this.lastYPosition = value;
      return true;
    }

    return false;
  }

  private handleTouchStart = (touchStartEvent: React.TouchEvent<HTMLTableSectionElement>) => {
    this.lastYPosition = touchStartEvent.touches[0].clientY;
  };

  private handleTouchMove = (touchMoveEvent: React.TouchEvent<HTMLTableSectionElement>) => {
    if (touchMoveEvent.touches.length === 1) {
      if (this.touchHandler(touchMoveEvent.touches[0].clientY)) touchMoveEvent.preventDefault();
    }
  };
  //KONEC Posouvání kurzoru v DataGridu pomocí gesta(myš/dotyk)

  private handleKeyDown = (e: React.KeyboardEvent<HTMLTableSectionElement>) => {
    if (e.shiftKey) {
      this.shiftFlag = true;
    }
    this.props.onKeyDown(e);
  };

  private handleKeyUp = (e: React.KeyboardEvent<HTMLTableSectionElement>) => {
    if (e.keyCode === 16) {
      this.shiftFlag = false;
    }
  };

  private handleWhell = (e: React.WheelEvent<HTMLTableSectionElement>) => {
    if (!this.shiftFlag) {
      if (e.deltaY > 0) {
        this.props.invokeAction(GridOperations.next, Math.round(this.props.rowHeight / 2));
      } else {
        this.props.invokeAction(GridOperations.prior, Math.round(this.props.rowHeight / 2));
      }
    }
  };
}

const K2Rows = withContext(Rows);

type RowRuleProps = K2RuleWithVCXProps & {
  rowHeight: number;
};
const dgRowRule = (props: RowRuleProps) => ({
  height: props.rowHeight + "px",
});

interface RowProps extends DGHeaderProps {
  data: CSDataItem;
  fixedColumnsCount: number;
  rowHeightMultiplier: number;
  getFixedColumnOffset: FixedColumnOffset;
  rowNdx: number;
  rowHeight: number;
  execute: (commandNumber: number) => void;
}

interface RowState {
  isSelected: boolean;
  inEditMode: boolean;
}

class Row extends React.Component<RowProps, RowState> {
  static displayName = `K2Row`;

  private columns: Map<number, Column>;
  private content: HTMLTableRowElement;

  constructor(props: RowProps) {
    super(props);
    this.state = { isSelected: false, inEditMode: false };
    this.columns = new Map<number, Column>();
  }

  private getClassName(): string {
    let clsName: string = this.props.renderer.renderRule(dgRowRule, { vcx: this.props.vcx, rowHeight: this.props.rowHeight });
    return clsName;
  }

  getOverRect(columnNdx: number): DOMRect {
    if (columnNdx !== undefined) {
      if (this.columns && this.columns.has(columnNdx)) {
        let column = this.columns.get(columnNdx);
        if (column) return column.getOverRect();
      }
      return null;
    }
    return this.content ? this.content.getBoundingClientRect() : undefined;
  }

  setAsActive(isActive: boolean) {
    //if(this.firstColumn) this.firstColumn.setAsActive();
    if (this.content && isActive) {
      this.content.focus();
    }
  }

  updateInEditMode(value: boolean) {
    this.setState({ inEditMode: value });
  }

  scrollTo(arg: boolean) {
    if (this.content) this.content.scrollIntoView(arg);
  }

  updateSelected(selected: boolean, oldColumn: number, newColumn: number) {
    this.setState({ isSelected: selected });
    let column: Column;
    if (oldColumn >= 0) {
      column = this.columns.get(oldColumn);
      if (column) {
        column.updateSelected(false);
      }
    }
    if (selected) {
      if (newColumn >= 0) {
        this.columns.forEach((column) => column.updateSelected(false));
        column = this.columns.get(newColumn);
        if (column) {
          column.updateSelected(true);
        }
      }
    }
  }

  componentDidUpdate(prevProps: RowProps, prevState: RowState) {
    if (this.state.isSelected === true && this.props.data !== prevProps.data) {
      this.setState({ isSelected: false });
    }
  }

  render() {
    let columns: any = null;
    if (this.props.data && this.props.data.Columns) {
      columns = this.props.data.Columns.map((value: CSColumn, index: number) => {
        if (index < this.props.columnsProportion.length) {
          return (
            <K2Column
              {...this.props}
              canEdit={this.state.inEditMode === true ? (value.CanEdit ? true : false) : undefined}
              isSelectedRow={this.state.isSelected}
              fixed={index < this.props.fixedColumnsCount}
              columnNdx={index}
              ref={(ref) => {
                if (ref && ref instanceof Column) this.columns.set(index, ref);
              }}
              key={"column_" + index}
              value={value}
              columnProportion={this.props.columnsProportion[index]}
              vcx={this.props.vcx}
              columnOffset={this.props.getFixedColumnOffset(this.props.columnsWidths, index, index < this.props.fixedColumnsCount)}
              rowHeightMultiplier={this.props.rowHeightMultiplier}
              execute={this.props.execute}
            />
          );
        } else {
          return null;
        }
      });
    }
    return (
      <tr
        data-k2-test-id={this.props.data.Key}
        tabIndex={1}
        className={this.getClassName()}
        ref={(ref) => {
          this.content = ref;
        }}
      >
        {columns}
      </tr>
    );
  }
} //getexterior

const K2Row = withContext(Row);

interface CellProps extends WithVCXProps {
  text?: string;
  multiTexts?: Array<MFValue>;
  measure?: MFMeasure;
  glyphId?: string;
  glyphId2?: string;
  isSelectedText?: boolean;
  isSelectedCell: boolean;
  isSelectedRow: boolean;
  isCancelled: boolean;
  isFixed: boolean;
  condFormatting: CondFormattingStyle;
  alignment: TAlignment;
  aligmentText?: TAlignment;
  isDenied?: TUpdateValueState;
  rowHeightMultiplier?: number;
  execute?: (commandNumber: number) => void;
}

class Cell extends React.PureComponent<CellProps, {}> {
  constructor(props: CellProps) {
    super(props);
  }

  render() {
    let textBackColor = this.props.isSelectedText ? this.props.vcx.getColor(this.props.vcx.Data.ColorMap.AccentBaseColorBck) : "";
    let textColor = this.props.isSelectedText ? this.props.vcx.getColor(this.props.vcx.Data.ColorMap.AccentBaseColorFrg) : "";
    let content: Array<JSX.Element> | JSX.Element;
    let useMultiText = false;

    if (this.props.multiTexts && this.props.multiTexts.length > 0) {
      content = (
        <K2MFText texts={this.props.multiTexts} measure={this.props.measure} vcx={this.props.vcx} renderer={this.props.renderer} execute={this.props.execute} />
      );
      useMultiText = true;
    } else {
      let text = (
        <K2TruncateText
          key="txt"
          keyboardAccelerator={false}
          textBackColor={textBackColor}
          textColor={textColor}
          style={{
            whiteSpace: this.props.rowHeightMultiplier > 1 ? "normal" : "nowrap",
            textAlign: this.props.glyphId && this.isTextEmpty() ? "center" : getTextAlign(this.props.alignment),
            width: this.props.glyphId && this.isTextEmpty() ? "auto" : "100%",
          }}
          className={
            this.props.rowHeightMultiplier > 1
              ? this.props.renderer.renderRule(clamp, { vcx: this.props.vcx, rowHeight: this.props.rowHeightMultiplier })
              : null
          }
        >
          {this.props.text?.replace(/\n|\t/g, "")}
        </K2TruncateText>
      );

      let img = this.props.glyphId && (
        <K2Img
          key="txtimg"
          glyphId={this.props.glyphId}
          vcx={this.props.vcx}
          height={this.props.vcx.GridControl.GetRowHeightWithoutMargin(this.props.rowHeightMultiplier)}
          style={{ width: "auto" }}
        />
      );

      let img2 = this.props.glyphId2 && (
        <K2Img
          key="txtimg2"
          glyphId={this.props.glyphId2}
          vcx={this.props.vcx}
          height={this.props.vcx.GridControl.GetRowHeightWithoutMargin(this.props.rowHeightMultiplier)}
          style={{ width: "auto" }}
        />
      );

      let alText = this.props.aligmentText ? this.props.aligmentText : this.props.alignment;
      if (img2) {
        content = [img2, img, text];
      } else {
        if (alText === TAlignment.taLeftJustify) {
          content = [text, img];
        } else if (alText === TAlignment.taRightJustify) {
          content = [img, text];
        } else {
          content = [text, img];
        }
      }
    }

    if (this.props.isDenied) {
      content = <div className={this.props.renderer.renderRule(deniedContent, { vcx: this.props.vcx, reason: this.props.isDenied })}></div>;
    }

    return (
      <div
        style={{ alignItems: "center", flex: "1 1 auto", justifyContent: "center" }}
        className={this.props.renderer.renderRule(cellRule, {
          vcx: this.props.vcx,
          isSelectedCell: this.props.isSelectedCell,
          isSelectedRow: this.props.isSelectedRow,
          isCancelled: this.props.isCancelled,
          condFormatting: this.props.condFormatting,
          isFixed: this.props.isFixed,
          img: this.props.glyphId && this.isTextEmpty(),
        })}
      >
        {content}
      </div>
    );
  }

  private isTextEmpty(): boolean {
    return this.props.text == undefined && this.props.multiTexts == undefined;
  }
}

export const K2Cell = withContext(Cell);

function getTextAlign(align: TAlignment): "center" | "left" | "right" {
  switch (align) {
    case TAlignment.taCenter:
      return "center";
    case TAlignment.taLeftJustify:
      return "left";
    case TAlignment.taRightJustify:
      return "right";
  }
}

function getContentAlign(align: TAlignment): "center" | "flex-start" | "flex-end" {
  switch (align) {
    case TAlignment.taCenter:
      return "center";
    case TAlignment.taLeftJustify:
      return "flex-start";
    case TAlignment.taRightJustify:
      return "flex-end";
  }
}

const dgColumnRule = (props: K2ColumnRuleProps) => ({
  cursor: "default",
  overflow: "hidden" as "hidden",
  verticalAlign: "middle",
  maxWidth: props.fixed ? null : 1, // todo aby se buňky tabulky zmenšovali i přes vlastní obsah
  height: "inherit",
});

const triangleRule = (props: K2RuleWithVCXProps & { color: string }) => ({
  width: 0,
  height: 0,
  borderRight: props.vcx.cssSizeMap(8) + " solid transparent",
  borderTop: props.vcx.cssSizeMap(8) + " solid " + props.color,
});

type K2CellProps = K2RuleWithVCXProps & {
  isCancelled: boolean;
  isSelectedCell: boolean;
  isSelectedRow: boolean;
  condFormatting: CondFormattingStyle;
  isFixed: boolean;
  img: boolean;
};

const cellRule = (props: K2CellProps) => ({
  //padding: props.glyphId && (props.proportions.DisplayStyleRT === TUFContextDisplayStyle.cdsImage || props.proportions.DisplayStyleRT === TUFContextDisplayStyle.cdsDefault) ? null : `0 ${column_padding_x}px`,
  padding: props.img ? 0 : `0 ${column_padding_x}px`,
  height: "100%",
  border: props.isSelectedCell ? "1px dotted " + props.vcx.getColor(props.vcx.Data.ColorMap.ContentFrame3) : "1px solid transparent",
  opacity: props.isCancelled == true ? 0.6 : "inherit",
  backgroundColor: ColumnBaseHelper.getBackgroundColor(props.condFormatting, props.isSelectedRow, props.isFixed, props.vcx),
  color: ColumnBaseHelper.getForegroundColor(props.condFormatting, props.isSelectedRow, props.isFixed, props.vcx),
  fontWeight: ColumnBaseHelper.IsBold(props.condFormatting) ? ("bold" as "bold") : ("normal" as "normal"),
  fontStyle: ColumnBaseHelper.IsItalic(props.condFormatting) ? ("italic" as "italic") : ("normal" as "normal"),
  textDecoration: getTextDecoration(ColumnBaseHelper.IsStrike(props.condFormatting), ColumnBaseHelper.IsUnderline(props.condFormatting)),
});

const clamp = (props: K2RuleWithVCXProps & { rowHeight: number }) => ({
  display: "-webkit-box",
  "-webkit-box-orient": "vertical",
  "-webkit-line-clamp": props.rowHeight,
  overflow: "hidden",
});

const asterisk = `<svg version="1.1" id="starmark" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 32 32" enable-background="new 0 0 32 32"><path id="starmark_1_" d="M13,29h2h2h2v-2v-5.8l5,2.9l1.8,1l1-1.8l1-1.8l1-1.7l-1.7-1L22,16l5-2.9l1.7-1l-1-1.7l-1-1.8l-1-1.8 l-1.8,1l-5,2.9V5V3h-2h-2h-2v2v5.8L8,7.9l-1.8-1l-1,1.8l-1,1.8l-1,1.7l1.7,1l5,2.9l-5,2.9l-1.7,1l1,1.7l1,1.8l1,1.8l1.8-1l5-2.9V27 V29L13,29z"></path></svg>`;
const questionMark = `<svg version="1.1" id="questionmark" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 32 32" enable-background="new 0 0 32 32"><path id="questionmark_1_" d="M17,22h-2v-2c0-2.2,1.8-4,4-4c1.1,0,2-0.9,2-2v-3c0-1.1-0.9-2-2-2h-4c-1.1,0-2,0.9-2,2v2h-2v-2 c0-2.2,1.8-4,4-4h4c2.2,0,4,1.8,4,4v3c0,2.2-1.8,4-4,4c-1.1,0-2,0.9-2,2V22z M17,24h-2v2h2V24z"></path></svg>`;

const deniedContent = (props: K2RuleWithVCXProps & { reason: TUpdateValueState }) => ({
  backgroundImage: `url('data:image/svg+xml;utf8,${props.reason === TUpdateValueState.uvsRefuseByPerm ? asterisk : questionMark}')`,
  width: "100%",
  height: "90%",
  opacity: 0.3,
});

interface ColumnProps extends HeaderColumnProps {
  value: CSColumn;
  fixed: boolean;
  canEdit: boolean;
  isSelectedRow: boolean;
  rowNdx: number;
  execute: (commandNumber: number) => void;
}

interface ColumnState {
  isSelectedColumn: boolean;
}

class Column extends React.PureComponent<ColumnProps, ColumnState> {
  static displayName = `K2Column`;
  private element: HTMLElement;

  constructor(props: ColumnProps) {
    super(props);
    this.state = { isSelectedColumn: false };
  }
  private getRule(): string {
    return this.props.renderer.renderRule(this.props.renderer.combineRule(dgColumnBaseRule, dgColumnRule), {
      fixed: this.props.fixed,
      proportion: this.props.columnProportion,
      vcx: this.props.vcx,
      offset: this.props.columnOffset,
    });
  }

  updateSelected(selectedCell: boolean) {
    this.setState({ isSelectedColumn: selectedCell });
  }

  setAsActive(isActive: boolean) {}

  getOverRect(): DOMRect {
    if (this.element) {
      this.element.scrollIntoView();
      return this.element.getBoundingClientRect();
    }

    return null;
  }

  render() {
    let triangle: JSX.Element = this.getStateTriangle();

    return (
      <td
        className={this.getRule()}
        onContextMenu={this.handleContextMenu}
        onClick={this.handleClick}
        ref={(ref) => {
          this.element = ref;
        }}
      >
        {triangle}
        <K2Cell
          vcx={this.props.vcx}
          text={this.props.value.Text}
          multiTexts={this.props.value.MultiTexts}
          measure={this.props.columnProportion.MFMeasure}
          glyphId={this.props.value.GlyphId}
          isSelectedText={this.props.value.IsSelectedText}
          isSelectedCell={this.state.isSelectedColumn}
          isSelectedRow={this.props.isSelectedRow}
          isCancelled={this.props.value.IsCancelled === true}
          isFixed={this.props.fixed}
          alignment={this.props.columnProportion.Alignment}
          condFormatting={this.props.value.CondFormatting}
          isDenied={this.props.value.UpdateValueState}
          rowHeightMultiplier={this.props.rowHeightMultiplier}
          execute={this.props.execute}
        />
      </td>
    );
  }

  private getStateTriangle(): JSX.Element {
    if (this.props.value) {
      if (this.props.value.IsModified || this.props.value.HasError) {
        return (
          <div
            className={this.props.renderer.renderRule(triangleRule, {
              vcx: this.props.vcx,
              color: this.props.value.HasError
                ? this.props.vcx.getColor(this.props.vcx.Data.ColorMap.ErrorColorBck)
                : this.props.vcx.getColor(this.props.vcx.Data.ColorMap.ContentChangeDecorateColorFrg),
            })}
          />
        );
      }
    }

    return null;
  }

  private timeout: any;
  private clickCount: number = 0;

  componentWillUnmount() {
    if (this.timeout) clearTimeout(this.timeout);
  }

  private handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (this.props.onColumnClick) {
      if (this.timeout) clearTimeout(this.timeout);

      this.clickCount++;
      let shiftKey = e.shiftKey;

      this.timeout = setTimeout(() => {
        if (this.clickCount >= 2) {
          this.props.onColumnClick.call(this, this.props.rowNdx, this.props.columnNdx, 2, shiftKey);
        } else {
          this.props.onColumnClick.call(this, this.props.rowNdx, this.props.columnNdx, 1, shiftKey);
        }
        this.clickCount = 0;
      }, 250);
      e.preventDefault();
    }
  };

  private handleContextMenu = (e: React.MouseEvent<HTMLTableDataCellElement>) => {
    if (this.props.onColumnContextMenu) {
      this.props.onColumnContextMenu.call(this, this.props.rowNdx, this.props.columnNdx, null);
    }
    e.preventDefault();
  };
}

const K2Column = withContext(Column);

const dgHeaderTableRule = (props: K2RuleWithVCXProps & { width?: number }) => ({
  borderCollapse: "collapse" as "collapse",
  width: props.width ? `${props.width}px` : "auto",
  height: props.vcx.GridControl.GetHeaderRowHeight(),
  minWidth: "100%",
  tableLayout: "fixed" as "fixed",
  flex: "0 0 auto",
});

const dgTableRule = (props: K2RuleWithVCXProps & { fillHeight?: boolean; width?: number }) => ({
  //flex:'auto',
  borderCollapse: "collapse" as "collapse",
  width: props.width ? `${props.width}px` : "auto",
  height: props.fillHeight ? "100%" : "auto",
  minWidth: "100%",
  tableLayout: "fixed" as "fixed",
  backgroundColor: props.inEditMode
    ? props.vcx.getColor(props.vcx.Data.ColorMap.DataChangeColorBck)
    : props.vcx.getColor(props.vcx.Data.ColorMap.DataBrowseColorBck),
});

const dgRule = (props: K2RuleWithVCXProps) => ({
  flex: "1 1 auto",
  flexDirection: "column" as "column",
});

const dgScrollContentrRule = (props: K2RuleWithVCXProps) => ({
  display: "flex",
  flex: 1,
  flexDirection: "column" as "column",
  width: "100%",
  height: "100%",
  overflowY: "hidden" as "hidden",
  overflowX: "auto" as "auto",
  backgroundColor: props.inEditMode
    ? props.vcx.getColor(props.vcx.Data.ColorMap.DataChangeColorBck)
    : props.vcx.getColor(props.vcx.Data.ColorMap.DataBrowseColorBck),
  touchAction: "pan-x",
});

const dgNoDataWarning = (props: K2RuleWithVCXProps) => ({
  margin: "auto",
  width: "100%",
  backgroundColor: props.vcx.getColor(props.vcx.Data.ColorMap.ContentFrameMin),
  padding: "6px 55% 3px 45%",
  fontSize: "80%",
});

interface DataGridProps extends WithContextPlacementProps {
  setActive: (isActive: boolean) => void;
}

interface InnerDataGridState extends K2ComponentState<UpdateInnerDataGrid> {
  Width: number;
  Height: number;
  clientWidthDiffs?: Array<number>;
  xDragPos: number;
  dragging: boolean;
  remount?: boolean; // pri zmene poctu fixnich sloupcu Chrome nespravne prekresloval tabulku, bylo nutne kompletne remoutnout nektere komponenty tabulky
}

class _InnerDataGrid extends React.PureComponent<DataGridProps, InnerDataGridState> implements DataGridListener, UFOverAlign {
  private rows: Rows;
  static displayName = `K2InnerDataGrid`;
  private control: NclInnerDataGrid;
  private requestSetAsActive: boolean;
  private listener: UFOverAlignListener;
  private columns: DOMRect[];
  private draggedColumnIndex: number;
  private hoveredColumnIndex: number;
  private scrollDiv = React.createRef<HTMLDivElement>();
  private tableHeader = React.createRef<HTMLDivElement>();
  private editingColumns: boolean;
  private table: HTMLTableElement;
  private content: HTMLDivElement;
  private lastTranslateY: number = 0;
  private firstUpdate: boolean = false;
  private scrollToRequest: { rowNdx: number; firstVisible: boolean };

  constructor(props: DataGridProps) {
    super(props);
    this.control = AcquireControl(this.props.controlUID, this.props.vrUID, (ctrl) => {
      return ctrl instanceof NclInnerDataGrid;
    }) as NclInnerDataGrid;

    this.state = { data: this.control.init(this) as UpdateInnerDataGrid, Width: 0, Height: 0, vcxVersion: -1, xDragPos: undefined, dragging: false };
  }

  registerOverAlignListener(listener: UFOverAlignListener): void {
    this.listener = listener;
  }

  unRegisterOverAlignListener(listener: UFOverAlignListener): void {
    if (this.listener === listener) {
      this.listener = null;
    }
  }

  getOverRect(): DOMRect {
    if (this.rows) {
      let row = this.rows.getRow(this.control.viewRowNdx);
      if (row) return row.getOverRect(this.control.ColNdx);
    }

    return null;
  }

  private containerRule = (props: K2RuleWithVCXProps) => ({
    flex: 1,
  });

  updateState(state: UpdateControl) {
    if (state instanceof UpdateInnerDataGrid) {
      if (state.ComputedColumnsVersion !== state.ColumnsVersion && this.state.Width > 0) {
        this.control.reCalculateColumnWidths(this.state.Width);
        this.setMaxRowCount(this.state.Height);
        return; // recalculate columns width and call update
      }

      if (state.ColumnsVersion !== this.state.data.ColumnsVersion) {
        this.setMaxRowCount(this.state.Height);
      }
    }

    this.setState((prevState: InnerDataGridState) => {
      let diffs = prevState.clientWidthDiffs;
      if (state instanceof UpdateInnerDataGrid) {
        if (state.CanEditItems !== prevState.data.CanEditItems) {
          this.updateCanEditItems(state.CanEditItems);
        }
        if (state.ColumnsVersion !== prevState.data.ColumnsVersion) {
          diffs = null;
        }

        return { data: state as UpdateInnerDataGrid, clientWidthDiffs: diffs };
      }
    });
  }

  private updateCanEditItems(value: boolean) {
    if (this.rows) {
      let row = this.rows.getRow(this.control.viewRowNdx);
      if (row) {
        row.updateInEditMode(value);
      }
    }
  }

  updatePosition(oldRow: number, newRow: number, oldCol: number, newCol: number) {
    if (this.rows && (oldRow !== newRow || oldCol !== newCol)) {
      let ar: Row;
      if (oldRow !== newRow) {
        ar = this.rows.getRow(oldRow);
        if (ar) {
          ar.updateSelected(false, oldCol, newCol);
        }
      }
      ar = this.rows.getRow(newRow);
      if (ar) {
        ar.updateSelected(true, oldCol, newCol);
        if (this.requestSetAsActive) {
          ar.setAsActive(true);
          this.requestSetAsActive = false;
        }
      }
      if (this.listener) {
        this.listener.updateAlignAnchor();
      }
    }
  }

  componentDidUpdate(prevProps: WithContextPlacementProps, prevState: InnerDataGridState) {
    if (prevState.Height != this.state.Height && this.scrollToRequest) {
      this.scrollTo(this.scrollToRequest.rowNdx, this.scrollToRequest.firstVisible);
      this.scrollToRequest = undefined;
    }

    if (!this.state.clientWidthDiffs && prevState.Width != this.state.Width && this.state.data.AutoAdjustWidth) {
      this.control.reCalculateColumnWidths(this.state.Width);
      this.setMaxRowCount(this.state.Height);
      return;
    }

    if (this.state.data.FixedColumnsCount > 0 && this.state.data.FixedColumnsCount !== prevState.data.FixedColumnsCount) {
      this.setState({ remount: true });
    }

    if (this.state.remount) {
      this.setState({ remount: false });
    }
    if (this.firstUpdate === true) {
      //set top and current index after grid first rendered
      this.scrollTo(this.control.topNdxOfView, true);
      let cmp = this.control.isInVisibleView(this.control.viewRowNdx);
      if (cmp === 0) {
        this.updatePosition(-1, this.control.viewRowNdx, -1, this.control.ColNdx);
      } else {
        if (this.control.moveViewTo(this.control.viewRowNdx, cmp > 0)) {
          let ar = this.scrollTo(this.control.viewRowNdx, cmp < 0);
          if (ar) {
            ar.updateSelected(true, -1, this.control.ColNdx);
          }
        }
      }
      this.firstUpdate = false;
    }

    if (prevState.data.DataVersion != this.state.data.DataVersion) {
      this.ignoreMove = false;
      this.scrollTo(this.control.topNdxOfView, true); //Po navratu z detailu se znovu renderuje list a nidko ho nenastavil na first
      if (this.control.viewRowNdx >= 0 && this.control.viewRowNdx < this.control.data.length) {
        this.updatePosition(-1, this.control.viewRowNdx, -1, this.control.ColNdx);
      }
    }

    if (prevState.data.Position !== this.state.data.Position) {
      //set current after position changed
      let cmp = this.control.isInVisibleView(this.control.viewRowNdx);
      if (cmp !== 0) {
        this.control.moveView(prevState.data.Position > this.state.data.Position ? GridOperations.prior : GridOperations.next, 1);
        this.scrollTo(this.control.viewRowNdx, cmp < 0);
      }
    }
  }

  private setMaxRowCount(height: number) {
    if (this.control.getRowHeight() > 0 && height > 0) {
      this.lastTranslateY = 0;
      let maxRowCount: number = height / this.control.getRowHeight();
      this.control.Container.setMaxRowCount(Math.floor(maxRowCount));
    }
  }

  componentDidMount() {
    if (this.control) {
      this.firstUpdate = true;
    }
    this.scrollDiv.current.addEventListener("touchmove", (e) => {
      if (this.editingColumns) {
        e.preventDefault();
      }
    });
  }

  updateVCX(vcxVersion: number) {
    this.setState({ vcxVersion: vcxVersion });
  }

  setAsActive(isActive: boolean) {
    if (isActive) {
      if (this.rows) {
        let cmp = this.control.isInVisibleView(this.control.viewRowNdx);
        if (cmp === 0) {
          let ar = this.rows.getRow(this.control.viewRowNdx);
          if (ar) {
            ar.setAsActive(isActive);
            return;
          }
        } else {
          this.props.setActive(true);
        }
      } else {
        this.requestSetAsActive = true;
      }
    } else {
      this.requestSetAsActive = false;
    }
  }

  componentWillUnmount() {
    this.control.willUnMount(true);
    this.control = null;
  }

  private getNoDataWarning(): JSX.Element {
    if (this.control.data.length > 0 && this.state.data.ColumnsProportion.length > 0) {
      return <div className={this.props.renderer.renderRule(dgNoDataWarning, { vcx: this.control.VCX })}>Žádné údaje</div>;
    }
    return;
  }

  private calcTotalWidth(): number {
    if ((!this.state.clientWidthDiffs || !this.state.data.AutoAdjustWidth) && !this.state.data.ColumnsProportion) return null;
    let result = 0;
    this.state.data.ColumnsProportion.map((value, i) => {
      result += value.Width;
    });

    if (this.state.clientWidthDiffs) {
      this.state.clientWidthDiffs.map((value) => {
        result += value;
      });
    }

    return result;
  }

  handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  handleDragEnter = (e: React.DragEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (!this.state.dragging) return;

    let xPos = 0;

    if (e.nativeEvent instanceof DragEvent) {
      xPos = (e as React.DragEvent).clientX;
    } else {
      xPos = (e as React.TouchEvent).touches[0].clientX;
    }

    let offsetLeft = e.currentTarget.getBoundingClientRect().left;
    this.columns?.map((column, index) => {
      if (xPos < column.right && index < this.state.data.FixedColumnsCount) return;
      if (xPos > column.left && xPos < column.right) {
        if (this.draggedColumnIndex >= index) {
          this.setState({ xDragPos: column.left - offsetLeft + this.scrollDiv.current.scrollLeft });
          this.hoveredColumnIndex = index;
        } else {
          this.setState({ xDragPos: column.right - offsetLeft + this.scrollDiv.current.scrollLeft });
          this.hoveredColumnIndex = index;
        }
      }
    });
  };

  handleDrop = () => {
    if (!this.state.dragging) return;

    this.editingColumns = false;

    if (this.draggedColumnIndex === this.hoveredColumnIndex) {
      this.setState({ dragging: false });
      return;
    }

    if (this.draggedColumnIndex == undefined) return;

    this.control.columnMove(this.draggedColumnIndex, this.hoveredColumnIndex, true);
    this.setState({ dragging: false });
    this.draggedColumnIndex = null;
  };

  setColumns = (options: { sizes: DOMRect[]; draggedColumnIndex: number }) => {
    this.columns = options.sizes;
    this.draggedColumnIndex = options.draggedColumnIndex;
    this.columns.map((column, index) => {
      if (this.draggedColumnIndex === index) {
        this.hoveredColumnIndex = index;
      }
    });
    this.setState({ dragging: true });
  };

  deleteColumn = () => {
    this.control.columnMove(this.draggedColumnIndex, -1, true);
    this.setState({ dragging: false });
  };

  getColumnsWidthsWithDiffs = (columns: ColumnProportionEm[]) => {
    let diff: number = 0;

    return columns.map((column, i) => {
      if (this.state.clientWidthDiffs && i < this.state.clientWidthDiffs.length) {
        diff = this.state.clientWidthDiffs[i];
      }

      return column.Width + diff;
    });
  };

  getFixedColumnOffset = (columnsWidths: number[], index: number, fixed: boolean): number => {
    let offset = 0;
    if (columnsWidths && fixed) {
      for (let i = 0; i < index; i++) {
        offset += columnsWidths[i];
      }
    }

    return offset;
  };

  handleEditColumns = (editing: boolean): boolean => {
    if (this.control.Ncl.CanColumnDrag === 1) {
      this.editingColumns = editing;
      return true;
    }
    return false;
  };

  render() {
    let columnsProportion: Array<ColumnProportionEm> = this.state.data.ColumnsProportion;
    let addAttributes: ElementHtmlAttributes = getAttributes(this.state.data);

    if (!columnsProportion) return null;
    let width = this.calcTotalWidth();
    let columnsWidthsWithDiffs: number[] = this.getColumnsWidthsWithDiffs(columnsProportion);

    return (
      <div
        style={StyleHelper(this.control, { position: "relative", ...this.props.style })}
        className={this.props.renderer.renderRule(dgRule, { vcx: this.control.VCX })}
        onFocus={this.handleFocus}
        {...addAttributes}
        onDragOver={this.handleDragOver}
        onDragEnter={this.handleDragEnter}
        onDrop={this.handleDrop}
        onTouchMove={this.handleDragEnter}
        onTouchEnd={this.handleDrop}
      >
        <div
          className={this.props.renderer.renderRule(dgScrollContentrRule, { vcx: this.control.VCX, inEditMode: this.state.data.CanEditItems })}
          ref={this.scrollDiv}
        >
          {this.state.dragging && this.hoveredColumnIndex !== undefined && (
            <div style={{ border: "1px solid", position: "absolute", left: this.state.xDragPos, height: "100%" }}></div>
          )}

          {!this.control.isHideHeading() && (
            <div style={{ flex: "0 0 auto" }}>
              <div ref={this.tableHeader} style={{ display: "block", overflow: "hidden" }}>
                <table className={this.props.renderer.renderRule(dgHeaderTableRule, { vcx: this.control.VCX, width: width })}>
                  {this.state.data.ColumnsProportion && (
                    <ColGroup
                      columnsProportion={this.state.data.ColumnsProportion}
                      fixedColumnsCount={this.state.data.FixedColumnsCount}
                      columnsWidths={columnsWidthsWithDiffs}
                      columnsVersion={this.state.data.ColumnsVersion}
                    />
                  )}
                  <thead>
                    {!this.state.remount && (
                      <K2DGHeader
                        key={this.control.Ncl.ControlUID + "_header"}
                        orderBy={this.state.data.OrderBy.toJS()}
                        columnsProportion={columnsProportion}
                        fixedColumnsCount={this.state.data.FixedColumnsCount}
                        columnsWidths={columnsWidthsWithDiffs}
                        columnsVersion={this.state.data.ColumnsVersion}
                        vcx={this.control.VCX}
                        onColumnContextMenu={this.handleContextMenu}
                        onColumnClick={this.handleHeaderColumnClick}
                        onColumnResize={this.handleColumnResize}
                        setColumns={this.setColumns}
                        deleteColumn={this.deleteColumn}
                        getFixedColumnOffset={this.getFixedColumnOffset}
                        editingColumns={this.handleEditColumns}
                      />
                    )}
                    {this.control.QuickFilter && (
                      <K2QuickFilter
                        vrUID={this.control.getRealizerUID()}
                        controlUID={this.control.QuickFilter.MetaData.ControlUID}
                        columnsProportion={columnsProportion}
                        fixedColumnsCount={this.state.data.FixedColumnsCount}
                        columnsWidths={columnsWidthsWithDiffs}
                        columnsVersion={this.state.data.ColumnsVersion}
                        getFixedColumnOffset={this.getFixedColumnOffset}
                      />
                    )}
                  </thead>
                </table>
              </div>
            </div>
          )}
          <div style={{ flex: "1 1 100%" }}>
            <ReactResizeDetector handleHeight handleWidth refreshMode={"debounce"} refreshRate={500} onResize={this.onResize} />

            <div ref={(ref) => (this.content = ref)} style={{ display: "block", overflowY: "hidden" }} onScroll={this.handleTableScroll}>
              <table
                ref={(ref) => (this.table = ref)}
                className={this.props.renderer.renderRule(dgTableRule, { vcx: this.control.VCX, inEditMode: this.state.data.CanEditItems, width: width })}
              >
                {this.state.data.ColumnsProportion && (
                  <ColGroup
                    columnsProportion={this.state.data.ColumnsProportion}
                    fixedColumnsCount={this.state.data.FixedColumnsCount}
                    columnsWidths={columnsWidthsWithDiffs}
                    columnsVersion={this.state.data.ColumnsVersion}
                  />
                )}
                {!this.state.remount && (
                  <K2Rows
                    invokeAction={this.handleInvokeAction}
                    onKeyDown={this.handleKeyDown}
                    onKeyPress={this.handleKeyPress}
                    key={this.control.Ncl.ControlUID + "_rows"}
                    rowHeightMultiplier={this.control.getRowHeightMultiplier()}
                    inEditMode={false /*this.control.InEditMode*/}
                    rows={this.control.data}
                    dataVersion={this.state.data.DataVersion}
                    ref={(rows) => {
                      this.rows = rows as Rows;
                    }}
                    vcx={this.control.VCX}
                    onColumnContextMenu={this.handleContextMenu}
                    onColumnClick={this.handleColumnClick}
                    columnsProportion={columnsProportion}
                    fixedColumnsCount={this.state.data.FixedColumnsCount}
                    columnsWidths={columnsWidthsWithDiffs}
                    columnsVersion={this.state.data.ColumnsVersion}
                    getFixedColumnOffset={this.getFixedColumnOffset}
                    rowHeight={this.control.getRowHeight()}
                    execute={this.control.executeCommandByNumber}
                  />
                )}
              </table>
            </div>
          </div>
          {this.control.Container.AggregationPanel && !this.state.remount && (
            <K2AggregationPanel
              controlUID={this.control.Container.AggregationPanel.MetaData.ControlUID}
              vrUID={this.control.getRealizerUID()}
              width={width}
              getFixedColumnOffset={this.getFixedColumnOffset}
              columnsProportion={columnsProportion}
              fixedColumnsCount={this.state.data.FixedColumnsCount}
              columnsWidths={columnsWidthsWithDiffs}
              columnsVersion={this.state.data.ColumnsVersion}
            />
          )}
          {/* {this.getNoDataWarning()} */}
        </div>
      </div>
    );
  }

  private handleTableScroll = (e: React.UIEvent) => {
    if (e.currentTarget) {
      if (this.tableHeader.current) {
        this.tableHeader.current.scrollLeft = e.currentTarget.scrollLeft;
      }
    }
  };

  private handleInvokeAction = (op: GridOperations, diffPX: number) => {
    if (this.control.isCacheEnabled()) {
      this.moveTo(op, diffPX);
      return;
    }

    this.control.invokeAction(op);
  };

  private isInVisibleView(ndx: number, diff: number): boolean {
    if (this.rows) {
      let ar = this.rows.getRow(ndx);
      if (ar) {
        let rect = ar.getOverRect(undefined);
        let tRect = this.content.getBoundingClientRect();
        if (rect && tRect) {
          return Math.ceil(tRect.top) <= Math.ceil(rect.top) + diff;
        }
      }
    }
    return false;
  }

  private ignoreMove: boolean = false;
  private moveTo(op: GridOperations, diffPX: number) {
    if (Context.getApplication().isBusy() || this.ignoreMove === true) return;
    if (this.table) {
      let translate: number;
      let diffRow = Math.max(Math.round(diffPX / this.control.getRowHeight()), 1);

      if (op === GridOperations.next) {
        translate = this.lastTranslateY - diffPX;
      } else {
        translate = this.lastTranslateY + diffPX;
      }

      let canChange: boolean;
      if (diffRow > 1) {
        canChange = true;
      } else {
        if (op === GridOperations.next) {
          canChange = !this.isInVisibleView(this.control.topNdxOfView, diffPX * -1);
          //|| (this.control.Data.length <= this.control.FirstNdxOfView + this.control.Container.MaxRowCount && this.isInVisibleView(this.control.FirstNdxOfView));
        } else {
          canChange = this.isInVisibleView(this.control.topNdxOfView - diffRow, diffPX) || this.control.topNdxOfView - diffRow <= 0;
        }
      }
      if (canChange) {
        this.ignoreMove = true;
        let result = this.control.moveView(op, Math.max(diffRow, 1));
        if (result === true) {
          this.lastTranslateY = translate;
          this.table.style.transform = `translateY(${translate}px)`;
          this.ignoreMove = false;
        } else if (result === false) {
          this.scrollTo(this.control.topNdxOfView, this.control.topNdxOfView <= 0);
        }
      } else {
        this.lastTranslateY = translate;
        this.table.style.transform = `translateY(${translate}px)`;
      }
    }
  }

  private scrollTo(ndx: number, firstVisible: boolean): Row {
    let ar = this.rows.getRow(ndx);
    if (ar) {
      if (this.state.Height <= 0) {
        this.scrollToRequest = { rowNdx: ndx, firstVisible: firstVisible };
      } else {
      this.table.style.transform = "";
      ar.scrollTo(firstVisible);
      this.lastTranslateY = 0;
      }
      return ar;
    }
    return undefined;
  }

  private handleContextMenu = (rowIndex: number, colIndex: number) => {
    this.control.contextMenu(rowIndex, colIndex);
  };

  private handleHeaderColumnClick = (rowIndex: number, colIndex: number, clickCount: number, shiftKey: boolean) => {
    this.control.Container.changeSortBy(colIndex, shiftKey);
  };

  private handleColumnClick = (rowIndex: number, colIndex: number, clickCount: number, shiftKey: boolean) => {
    this.control.click(rowIndex, colIndex, clickCount);
  };

  private handleColumnResize = (colIndex: number, diffX: number, isCompleted: boolean) => {
    let diffs: Array<number>;
    if (isCompleted) {
      if (this.state.data.ColumnsProportion && colIndex < this.state.data.ColumnsProportion.length) {
        if (this.state.clientWidthDiffs) {
          diffs = [...this.state.clientWidthDiffs];
        } else {
          diffs = [];
          this.state.data.ColumnsProportion.map((value, i) => {
            diffs[i] = 0;
          });
        }
      }
      if (diffs) {
        diffs[colIndex] += diffX;
        if (this.state.data.ColumnsProportion[colIndex].Width + diffs[colIndex] <= 10) return; //check minimum
        this.control.setColumnsWidth(diffs, this.state.data.ColumnsProportion);
        this.setState({ clientWidthDiffs: diffs });
      }
    }
  };

  private handleFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    this.control.Container.setActiveControlRequested();
    e.stopPropagation();
  };

  private handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowUp") {
      this.control.invokeAction(GridOperations.prior);
      e.stopPropagation();
      return false;
    }

    if (e.key === "ArrowDown") {
      if (e.shiftKey) return false;

      this.control.invokeAction(GridOperations.next);
      e.stopPropagation();
      return false;
    }

    if (e.key === "ArrowLeft" && this.control.ColNdx - 1 >= 0) {
      this.control.click(undefined, this.control.ColNdx - 1);
      e.stopPropagation();
      return false;
    }

    if (e.key === "ArrowRight" && this.state.data.ColumnsProportion && this.control.ColNdx + 1 < this.state.data.ColumnsProportion.length) {
      this.control.click(undefined, this.control.ColNdx + 1);
      e.stopPropagation();
      return false;
    }

    if (e.key === "PageDown") {
      this.control.invokeAction(GridOperations.nextPage);
      e.stopPropagation();
      return false;
    }

    if (e.key === "PageUp") {
      this.control.invokeAction(GridOperations.priorPage);
      e.stopPropagation();
      return false;
    }

    if (e.ctrlKey && e.key === "Home") {
      this.control.invokeAction(GridOperations.firstPage);
      e.stopPropagation();
      return false;
    }

    if (e.ctrlKey && e.key === "End") {
      this.control.invokeAction(GridOperations.lastPage);
      e.stopPropagation();
      return false;
    }

    if (e.ctrlKey && e.key === "c") {
      if (this.control.data && this.control.viewRowNdx < this.control.data.length) {
        const row = this.control.data[this.control.viewRowNdx];
        if (row && row.Columns && this.control.ColNdx < row.Columns.length) {
          const text = row.Columns[this.control.ColNdx]?.Text;

          if (text) {
            this.control.copy(text);
          }
        }
      }
    }

    let x = e.keyCode | e.which;
    if (x === 8 && this.control.Container.Header && this.control.Container.Header.LocatorPanel) {
      //backspace
      let locator: NclInput = this.control.Container.Header.LocatorPanel.getLastLocatrInput();
      if (locator && locator.Listener && locator.Listener instanceof _Input) {
        locator.Listener.setAsActive(true);
        locator.Listener.handleKeyDown(e);
      }
    }
  };

  private handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isKeyOrDigit(e.key) && this.control.Container.Header && this.control.Container.Header.LocatorPanel) {
      let locator: NclInput = this.control.Container.Header.LocatorPanel.getLastLocatrInput();
      if (locator && locator.Listener && locator.Listener instanceof _Input) {
        locator.Listener.setAsActive(true);
        locator.Listener.handleKeyPress(e);
      }
    }
  };

  private onResize = (width: number, height: number) => {
    if ((height > 0 || width > 0) && (width != this.state.Width || height != this.state.Height)) {
      if (height != this.state.Height) {
        if (this.control.getRowHeight() > 0) {
          this.setMaxRowCount(height);
        } else {
          let vr = ViewRealizerManager.getViewRealizer(this.control.getRealizerUID()); //if don't know height of row, must call update for columns
          if (vr) vr.sendRequest(RealizerOperations.Update);
        }
      }
      this.setState({ Width: width, Height: height });
    }
  };
}

const K2InnerDataGrid = withContext(_InnerDataGrid);

const dgContentRule = (props: K2RuleWithVCXProps) => ({
  flex: "1 1 auto",
  flexDirection: "column" as "column",
});

class _DataGridContent extends React.Component<DataGridProps, K2ComponentState<UpdateControl>> {
  static displayName = `K2DataGridContent`;
  private control: NclDataGridContent;

  constructor(props: DataGridProps) {
    super(props);
    this.control = AcquireControl(this.props.controlUID, this.props.vrUID, (ctrl) => {
      return ctrl instanceof NclDataGridContent;
    }) as NclDataGridContent;
    this.state = { data: this.control.init(this) as UpdateControl, vcxVersion: -1 };
  }

  updateState(state: UpdateControl) {
    this.setState((prevState: K2ComponentState<UpdateControl>) => {
      return { data: state as UpdateControl };
    });
  }

  updateVCX(vcxVersion: number) {
    this.setState({ vcxVersion: vcxVersion });
  }

  componentWillUnmount() {
    this.control.willUnMount(true);
    this.control = null;
  }

  render() {
    return (
      <div style={StyleHelper(this.control, this.props.style)} className={this.props.renderer.renderRule(dgContentRule, { vcx: this.control.VCX })}>
        <K2InnerDataGrid controlUID={this.control.DataGrid.MetaData.ControlUID} vrUID={this.control.getRealizerUID()} setActive={this.props.setActive} />
      </div>
    );
  }
}

const K2DataGridContent = withContext(_DataGridContent);

const dgFooterRule = (props: K2RuleWithVCXProps) => ({
  flex: "none",
  background: props.vcx.getColor(props.vcx.Data.ColorMap.ContentColorBck1),
  height: props.vcx.ExpanderControl.GetHFHeight() + "px",
  color: props.vcx.getColor(props.vcx.Data.ColorMap.BaseColorBck1),
  border: "1px solid " + props.vcx.getColor(props.vcx.Data.ColorMap.ContentFrame1),
});

class _DataGridFooter extends React.Component<WithContextPlacementProps, K2ComponentState<UpdateControl>> {
  static displayName = `K2DataGridFooter`;
  private control: NclDataGridFooter;

  constructor(props: WithContextPlacementProps) {
    super(props);
    this.control = AcquireControl(this.props.controlUID, this.props.vrUID, (ctrl) => {
      return ctrl instanceof NclDataGridFooter;
    }) as NclDataGridFooter;
    this.state = { data: this.control.init(this) as UpdateControl, vcxVersion: -1 };
  }

  updateState(state: UpdateControl) {
    this.setState((prevState: K2ComponentState<UpdateControl>) => {
      return { data: state as UpdateControl };
    });
  }

  updateVCX(vcxVersion: number) {
    this.setState({ vcxVersion: vcxVersion });
  }

  componentWillUnmount() {
    this.control.willUnMount(true);
    this.control = null;
  }

  shouldComponentUpdate(nextProps: WithContextPlacementProps, nextState: K2ComponentState<UpdateControl>, nextContext: any): boolean {
    if (this.props.controlUID !== nextProps.controlUID) {
      return true;
    }

    if (this.props.vrUID !== nextProps.vrUID) {
      return true;
    }

    if (this.state !== nextState) {
      return true;
    }

    return false;
  }

  render() {
    return (
      <div style={StyleHelper(this.control, this.props.style)} className={this.props.renderer.renderRule(dgFooterRule, { vcx: this.control.VCX })}>
        <K2ToolBar controlUID={this.control.LeftToolbar.MetaData.ControlUID} vrUID={this.control.getRealizerUID()} />
        <K2ToolBar
          controlUID={this.control.RightToolbar.MetaData.ControlUID}
          vrUID={this.control.getRealizerUID()}
          style={{ justifyContent: "flex-end", width: "auto" }}
        />
      </div>
    );
  }
}

const K2DataGridFooter = withContext(_DataGridFooter);

class _DataGrid extends React.PureComponent<WithContextPlacementProps, K2ComponentState<UpdateDataGrid>> implements UFOverAlign {
  private control: NclDataGrid;
  static displayName = `K2DataGrid`;
  private element: HTMLDivElement;

  constructor(props: WithContextPlacementProps) {
    super(props);
    this.control = AcquireControl(this.props.controlUID, this.props.vrUID, (ctrl) => {
      return ctrl instanceof NclDataGrid;
    }) as NclDataGrid;
    this.state = { data: this.control.init(this) as UpdateDataGrid, vcxVersion: -1 };
  }

  getOverRect(): DOMRect {
    if (this.control && this.control.Content && this.control.Content.DataGrid && this.control.Content.DataGrid.Listener) {
      let listener: UFOverAlign = this.control.Content.DataGrid.Listener;
      if (listener.getOverRect) {
        return listener.getOverRect();
      }
    }

    return null;
  }

  registerOverAlignListener(listener: UFOverAlignListener) {
    if (this.control && this.control.Content && this.control.Content.DataGrid && this.control.Content.DataGrid.Listener) {
      let l: UFOverAlign = this.control.Content.DataGrid.Listener;

      if (l.registerOverAlignListener) {
        l.registerOverAlignListener(listener);
      }
    }
  }

  unRegisterOverAlignListener(listener: UFOverAlignListener) {
    if (this.control && this.control.Content && this.control.Content.DataGrid && this.control.Content.DataGrid.Listener) {
      let l: UFOverAlign = this.control.Content.DataGrid.Listener;

      if (l.registerOverAlignListener) {
        l.unRegisterOverAlignListener(listener);
      }
    }
  }

  updateState(state: UpdateControl) {
    this.setState((prevState: K2ComponentState<UpdateDataGrid>) => {
      return { data: state as UpdateDataGrid };
    });
  }

  updateVCX(vcxVersion: number) {
    this.setState({ vcxVersion: vcxVersion });
  }

  setAsActive = (isActive: boolean) => {
    if (this.element && isActive) {
      this.element.focus();
    }
  };

  componentWillUnmount() {
    this.control.willUnMount(true);
    this.control = null;
  }

  render() {
    let addAttributes: ElementHtmlAttributes = getAttributes(this.state.data);
    return (
      <div
        ref={(ref) => {
          this.element = ref;
        }}
        data-k2-test-id={this.control.Ncl.Name}
        style={StyleHelper(this.control, { ...this.props.style, minHeight: this.control.ComputedMinHeightWithMargin + "px" })}
        className={this.props.renderer.renderRule(dgRule, { vcx: this.control.VCX })}
        onFocus={this.handleFocus}
        {...addAttributes}
      >
        {this.control.isShowHeader() && (
          <K2Header
            controlUID={this.control.Header.MetaData.ControlUID}
            vrUID={this.props.vrUID}
            title={this.state.data.Title}
            titleSuffix={this.state.data.TitleSuffix}
            titleSuffixCommandEnabled={this.state.data.TitleSuffixCommandEnabled}
          />
        )}
        <K2DataGridContent controlUID={this.control.Content.MetaData.ControlUID} vrUID={this.props.vrUID} setActive={this.setAsActive} />
        {this.control.showFooter() && <K2DataGridFooter controlUID={this.control.Footer.MetaData.ControlUID} vrUID={this.control.getRealizerUID()} />}
      </div>
    );
  }

  /*shouldComponentUpdate(nextProps:  WithContextPlacementProps<NclDataGrid>, nextState: K2ComponentState<UpdateBaseGrid>, nextContext: any):boolean{
		let result : boolean = true;
		if(shouldUpdate(this.control.VCX)) return true;
		if(this.state.data.Data != nextState.data.Data) return result;
		if(this.state.data.Columns != nextState.data.Columns) return result;
		
		if(this.state.data.Position != nextState.data.Position && this.state.data.RowCount != 0){
			if(this.rows != null){
				this.rows.setState({position:nextState.data.Position});
				result = false;
			}
		}

		return result;
	}*/

  private handleFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    this.control.setActiveControlRequested();
    e.stopPropagation();
  };
}

export const K2DataGrid = withContext(_DataGrid);

class ColGroup extends React.Component<ColumnsProps> {
  constructor(props: ColumnsProps) {
    super(props);
  }

  shouldComponentUpdate(nextProps: HeaderProps, nextState: any, nextContext: any): boolean {
    if (nextProps.columnsVersion !== this.props.columnsVersion) {
      return true;
    }

    if (!compareNumberArray(this.props.columnsWidths, nextProps.columnsWidths)) {
      return true;
    }

    return false;
  }

  render() {
    return (
      <colgroup>
        {this.props.columnsProportion.map((value, i) => {
          if (this.props.fixedColumnsCount > 0 && i < this.props.fixedColumnsCount - 1) {
            return;
          } else if (this.props.fixedColumnsCount > 0 && i === this.props.fixedColumnsCount - 1) {
            let columnWidth = 0;

            for (let i = 0; i < this.props.fixedColumnsCount; i++) {
              columnWidth += this.props.columnsWidths[i];
            }

            return (
              <col
                data-k2-test-id={Context.DeviceInfo.IsTestMode === true ? value.FieldPath : undefined}
                key={`col__${i}`}
                style={{ width: `${columnWidth}px` }}
              />
            );
          }

          return (
            <col
              data-k2-test-id={Context.DeviceInfo.IsTestMode === true ? value.FieldPath : undefined}
              key={`col__${i}`}
              style={{ width: `${this.props.columnsWidths[i]}px` }}
            />
          );
        })}
      </colgroup>
    );
  }
}
